import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { MultiLineChartLine } from '../../MultiLineChart.types';
import styles from './MultiLineChartLegend.module.scss';

type MultiLineChartLegendProps = {
  lines: MultiLineChartLine[];
};

const measureText = (text: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return 0;
  }

  context.font = '400 12px nort';
  return context.measureText(text).width;
};

export const MultiLineChartLegend = ({ lines }: MultiLineChartLegendProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [multipleRows, setMultipleRows] = useState<MultiLineChartLine[][]>([]);

  const calculateLayout = () => {
    const gap = 32;
    const containerWidth = containerRef?.current?.clientWidth;

    if (!containerWidth) {
      return;
    }

    const itemsWidths = lines
      .map(line => measureText(line.label) + gap)
      .reduce((sum, acc) => sum + acc, 0);

    if (itemsWidths > containerWidth) {
      const isOdd = lines.length % 2 !== 0;
      // in case of odd legend number we want to have n+1 items in first row
      const midIndex = Math.floor(lines.length / 2) + (isOdd ? 1 : 0);
      setMultipleRows([lines.slice(0, midIndex), lines.slice(midIndex)]);
    } else {
      setMultipleRows([]);
    }
  };

  useEffect(() => {
    calculateLayout();

    window.addEventListener('resize', calculateLayout);
    return () => window.removeEventListener('resize', calculateLayout);
  }, [lines]);

  return (
    <div
      className={clsx(styles.legendContainer, {
        [styles.legendContainerMultipleRows]: !!multipleRows.length,
      })}
      ref={containerRef}
      data-testid="multi-line-chart-legend"
    >
      {!multipleRows.length
        ? lines.map(row => (
            <div className={styles.legendItem} key={row.label}>
              <div
                className={styles.legendColor}
                style={{ backgroundColor: row.color }}
              />
              <div>{row.label}</div>
            </div>
          ))
        : multipleRows.map((row, i) => (
            <div className={styles.multiRowWrapper} key={i}>
              {row.map(item => (
                <div className={styles.legendItem} key={item.label}>
                  <div
                    className={styles.legendColor}
                    style={{ backgroundColor: item.color }}
                  />
                  <div>{item.label}</div>
                </div>
              ))}
            </div>
          ))}
    </div>
  );
};
